import React, { useEffect, useRef, useState } from 'react';
import {
  AlertCircle, Check, CheckCheck, Clock, Copy, Link2, MoreHorizontal, Pencil, RotateCw, SmilePlus, Trash2, Unlink,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useChatContext } from 'stream-chat-react';
import PowerBIFilterShareMessage from './PowerBIFilterShareMessage';
import NativeReportShareMessage from './NativeReportShareMessage';
import AIChatShareMessage from './AIChatShareMessage';
import { FileCard, ImageGrid, LinkPreview, LodgeicLinkCard } from './MessageAttachments';
import ChatAvatar from '../parts/ChatAvatar';
import EmojiPicker from '../parts/EmojiPicker';
import { useChatMedia } from '../parts/ChatMediaContext';
import {
  describeAttachment, describeLodgeicLink, firstName, formatClock, formatShortClock, linkifyParts,
} from '../utils/chatFormat';
import { QUICK_REACTIONS, emojiInfo, reactorSentence, summarizeReactions, typeForEmoji } from '../utils/reactions';
import styles from '../Chat.module.css';

const SHARE_RENDERERS = {
  pbi_filter_share: PowerBIFilterShareMessage,
  native_report_share: NativeReportShareMessage,
  ai_session_share: AIChatShareMessage,
};

const conversationLink = (channel) => `${window.location.origin}/chat?cid=${encodeURIComponent(channel?.cid || '')}`;

function MessageText({ text, pending }) {
  const navigate = useNavigate();
  const parts = linkifyParts(text);
  return (
    <p className={`${styles.msgText} ${pending ? styles.pending : ''}`}>
      {parts.map((part, i) => {
        if (part.type === 'text') return <React.Fragment key={i}>{part.value}</React.Fragment>;
        const app = describeLodgeicLink(part.value);
        return (
          <a
            key={i}
            href={part.value}
            className={styles.msgLink}
            target={app ? undefined : '_blank'}
            rel={app ? undefined : 'noopener noreferrer'}
            onClick={app ? (e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey) return;
              e.preventDefault();
              navigate(app.path);
              window.dispatchEvent(new CustomEvent('lodgeic:chat:navigated'));
            } : undefined}
          >
            {part.value.replace(/^https?:\/\//, '')}
          </a>
        );
      })}
    </p>
  );
}

function StatusLine({ pendingStatus, receipt, onRetry, onDiscard }) {
  if (pendingStatus === 'sending') {
    return <span className={styles.statusLine}><Clock size={12} aria-hidden="true" />Sending…</span>;
  }
  if (pendingStatus === 'failed') {
    return (
      <span className={`${styles.statusLine} ${styles.failed}`}>
        <AlertCircle size={13} aria-hidden="true" />
        Not sent
        <button type="button" className={`${styles.btnOutline} ${styles.btnSm}`} style={{ height: 26, marginLeft: 6 }} onClick={onRetry}>
          <RotateCw size={12} aria-hidden="true" /> Retry
        </button>
        <button type="button" className={`${styles.btnGhost} ${styles.btnSm}`} style={{ height: 26 }} onClick={onDiscard}>Discard</button>
      </span>
    );
  }
  if (!receipt) return null;
  const { readers, isDM, othersCount } = receipt;
  if (!readers.length) {
    return <span className={styles.statusLine}><Check size={13} aria-hidden="true" />Sent</span>;
  }
  if (isDM) {
    return (
      <span className={styles.statusLine}>
        <CheckCheck size={13} aria-hidden="true" />
        Seen{readers[0].lastRead ? ` ${formatClock(readers[0].lastRead)}` : ''}
      </span>
    );
  }
  const names = readers.map((r) => firstName(r));
  const sentence = readers.length === othersCount && othersCount > 3
    ? 'Seen by everyone'
    : `Seen by ${reactorSentence({ names, count: readers.length })}`;
  return (
    <span className={styles.statusLine}>
      <span className={styles.receiptAvatars} aria-hidden="true">
        {readers.slice(0, 3).map((r) => (
          <ChatAvatar key={r.id} name={r.name || r.id} id={r.id} image={r.image} size="xs" />
        ))}
      </span>
      <CheckCheck size={13} aria-hidden="true" />
      {sentence}
    </span>
  );
}

const CustomMessageItem = ({
  message,
  isOwn,
  showHeader,
  channel,
  currentUserId,
  conversationName,
  isGroup = false,
  mediaItems = [],
  receipt,
  pendingStatus,
  onRetry,
  onDiscard,
  onToggleReaction,
  flash = false,
}) => {
  const { client } = useChatContext();
  const { openMedia } = useChatMedia();
  const rowRef = useRef(null);
  const editRef = useRef(null);
  const [picker, setPicker] = useState(false);
  const [menu, setMenu] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [tipFor, setTipFor] = useState(null);
  const [touchEngaged, setTouchEngaged] = useState(false);

  const isPending = Boolean(pendingStatus);
  const isDeleted = message.type === 'deleted' || Boolean(message.deleted_at);
  const engaged = picker || menu || confirmDelete || touchEngaged;

  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (e) => { if (rowRef.current && !rowRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menu]);

  useEffect(() => {
    if (!touchEngaged) return undefined;
    const onDown = (e) => { if (rowRef.current && !rowRef.current.contains(e.target)) setTouchEngaged(false); };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [touchEngaged]);

  // Pop-overs hang off the button that opened them: below it when there's
  // room in the conversation, above it otherwise; left- or right-aligned to it.
  const anchorFrom = (trigger, height, width) => {
    const row = rowRef.current;
    if (!row || !trigger) return { top: 20, right: 20 };
    const scroller = row.closest('[data-chat-scroller]');
    const r = row.getBoundingClientRect();
    const t = trigger.getBoundingClientRect();
    const s = scroller ? scroller.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
    const below = s.bottom - t.bottom;
    const above = t.top - s.top;
    const up = below < height && above > below;
    const vertical = up
      ? { top: 'auto', bottom: Math.round(r.bottom - t.top + 6) }
      : { top: Math.round(t.bottom - r.top + 6), bottom: 'auto' };
    // Kept inside the row so the conversation's overflow never clips it.
    const maxOffset = Math.max(8, r.width - width - 8);
    const horizontal = t.left - r.left < r.width / 2
      ? { left: Math.round(Math.min(Math.max(8, t.left - r.left), maxOffset)), right: 'auto' }
      : { right: Math.round(Math.min(Math.max(8, r.right - t.right), maxOffset)), left: 'auto' };
    return { ...vertical, ...horizontal };
  };
  const [popPos, setPopPos] = useState(null);
  const popStyle = popPos || { top: 20, right: 20 };

  const openPicker = (e) => { setPopPos(anchorFrom(e?.currentTarget, 380, 332)); setMenu(false); setPicker(true); };
  const openMenu = (e) => { setPopPos(anchorFrom(e?.currentTarget, 280, 260)); setPicker(false); setMenu((v) => !v); };

  const react = (char) => {
    setPicker(false);
    const type = typeForEmoji(char);
    if (type) onToggleReaction?.(message, type, char);
  };

  const startEdit = () => {
    setMenu(false);
    setDraft(message.text || '');
    setEditing(true);
  };

  // Caret at the end of the text, like any editor.
  useEffect(() => {
    if (!editing || !editRef.current) return;
    const el = editRef.current;
    el.focus({ preventScroll: true });
    el.setSelectionRange(el.value.length, el.value.length);
  }, [editing]);

  const saveEdit = async () => {
    const text = draft.trim();
    if (!text && !(message.attachments || []).length) return;
    if (text === (message.text || '').trim()) { setEditing(false); return; }
    setBusy(true);
    try {
      await client.partialUpdateMessage(message.id, { set: { text } });
      setEditing(false);
    } catch (error) {
      console.error('[CustomMessageItem] Error editing message:', error);
      toast.error('Could not save your edit.');
    } finally {
      setBusy(false);
    }
  };

  const deleteMessage = async () => {
    setBusy(true);
    try {
      await client.deleteMessage(message.id);
      setConfirmDelete(false);
    } catch (error) {
      console.error('[CustomMessageItem] Error deleting message:', error);
      toast.error(error?.status === 403 ? 'You can only delete your own messages.' : 'Could not delete this message.');
    } finally {
      setBusy(false);
    }
  };

  const copy = async (value, label) => {
    setMenu(false);
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch (_) {
      toast.error('Could not copy to the clipboard.');
    }
  };

  const described = (message.attachments || []).map((att, i) => ({ ...describeAttachment(att), key: `${message.id}:${i}`, index: i }));
  const shareTypes = Array.from(new Set(described.filter((d) => d.kind === 'share').map((d) => d.att.type)));
  const images = described.filter((d) => d.kind === 'image' && d.url);
  const files = described.filter((d) => d.kind === 'file');
  const appUrls = Array.from(new Set(linkifyParts(message.text).filter((p) => p.type === 'link').map((p) => p.value)))
    .filter((url) => describeLodgeicLink(url))
    .slice(0, 2);
  const links = described.filter((d) => d.kind === 'link' && !describeLodgeicLink(d.url));

  const removeLinkPreviews = async () => {
    setMenu(false);
    const keep = (message.attachments || []).filter((att) => describeAttachment(att).kind !== 'link');
    try {
      await client.partialUpdateMessage(message.id, { set: { attachments: keep } }, undefined, { skip_enrich_url: true });
    } catch (error) {
      console.error('[CustomMessageItem] Error removing link preview:', error);
      toast.error('Could not remove the preview.');
    }
  };

  const openItem = (d) => {
    const index = mediaItems.findIndex((m) => m.key === d.key);
    if (index >= 0) {
      openMedia(mediaItems, index, { conversation: isGroup ? conversationName : null });
    } else if (d.viewerType && d.url) {
      openMedia([{
        key: d.key, messageId: message.id, url: d.url, thumb: d.kind === 'image' ? d.thumb : null, fileType: d.viewerType,
        fileName: d.name, ext: d.ext, sender: message.user?.name, createdAt: message.created_at, size: d.size,
      }], 0, { conversation: isGroup ? conversationName : null });
    }
  };

  const reactions = isDeleted ? [] : summarizeReactions(message, currentUserId);
  const senderName = message.user?.name || message.user?.id || 'Unknown';
  const edited = Boolean(message.message_text_updated_at);
  const canEdit = isOwn && !isPending && !isDeleted && Boolean(message.text);
  const hasText = Boolean(message.text?.trim());

  const rowClass = [
    styles.messageRow,
    !showHeader && styles.continued,
    engaged && styles.engaged,
    flash && styles.flash,
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={rowRef}
      className={rowClass}
      data-message-id={message.id}
      onPointerUp={(e) => {
        if (e.pointerType === 'touch' && !isDeleted && !isPending && e.target === e.currentTarget) setTouchEngaged((v) => !v);
      }}
    >
      {showHeader ? (
        <ChatAvatar name={senderName} id={message.user?.id} image={message.user?.image} />
      ) : (
        <span className={styles.gutter} aria-hidden="true">
          <span className={styles.gutterTime}>{formatShortClock(message.created_at)}</span>
        </span>
      )}

      <div className={styles.msgBody}>
        {showHeader && (
          <div className={styles.msgHead}>
            <span className={styles.msgName}>{senderName}</span>
            {isOwn && <span className={styles.youTag}>You</span>}
            <span className={styles.msgTime}>{formatClock(message.created_at)}</span>
            {edited && !isDeleted && <span className={styles.msgTime}>(edited)</span>}
          </div>
        )}

        {isDeleted ? (
          <span className={styles.deletedNote}>
            <Trash2 size={13} aria-hidden="true" />
            {isOwn ? 'You deleted a message' : 'This message was deleted'}
          </span>
        ) : editing ? (
          <div className={styles.editBox}>
            <label>
              <span className={styles.srOnly}>Edit message</span>
              <textarea
                className={styles.editTextarea}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                  if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
                }}
                ref={editRef}
                rows={Math.min(6, Math.max(2, draft.split('\n').length))}
              />
            </label>
            <div className={styles.editActions}>
              <span className={styles.editHint}>Enter to save · Esc to cancel</span>
              <button type="button" className={`${styles.btnOutline} ${styles.btnSm}`} onClick={() => setEditing(false)}>Cancel</button>
              <button type="button" className={`${styles.btnInk} ${styles.btnSm}`} onClick={saveEdit} disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {hasText && <MessageText text={message.text} pending={pendingStatus === 'sending'} />}
            {!showHeader && edited && <span className={styles.msgTime} style={{ marginTop: -4 }}>(edited)</span>}
            {shareTypes.map((type) => {
              const Share = SHARE_RENDERERS[type];
              return Share ? <Share key={type} message={message} /> : null;
            })}
            {appUrls.map((url) => <LodgeicLinkCard key={url} url={url} />)}
            {links.map((link) => (
              <LinkPreview key={link.key} link={link} onRemove={isOwn && !isPending ? removeLinkPreviews : undefined} />
            ))}
            <ImageGrid images={images} onOpen={openItem} />
            {files.map((file) => <FileCard key={file.key} file={file} onOpen={openItem} />)}
          </>
        )}

        {reactions.length > 0 && (
          <div className={styles.reactions}>
            {reactions.map((r) => (
              <button
                key={r.type}
                type="button"
                className={`${styles.reactionChip} ${r.mine ? styles.mine : ''}`}
                aria-pressed={r.mine}
                aria-label={`${emojiInfo(r.emoji)?.name || r.type}: ${reactorSentence(r)}${r.mine ? ' (click to remove yours)' : ''}`}
                onClick={() => onToggleReaction?.(message, r.type, r.emoji)}
                onMouseEnter={() => setTipFor(r.type)}
                onMouseLeave={() => setTipFor(null)}
                onFocus={() => setTipFor(r.type)}
                onBlur={() => setTipFor(null)}
              >
                <span className={styles.reactionEmoji}>{r.emoji}</span>
                <span>{r.count}</span>
                {tipFor === r.type && (
                  <span className={styles.reactionTip} role="tooltip">
                    <strong>{reactorSentence(r)}</strong> reacted with {r.emoji}
                  </span>
                )}
              </button>
            ))}
            {!isPending && (
              <button type="button" className={styles.reactionAdd} onClick={openPicker} aria-label="Add reaction">
                <SmilePlus size={14} />
              </button>
            )}
          </div>
        )}

        {isOwn && (pendingStatus || receipt) && !isDeleted && (
          <StatusLine pendingStatus={pendingStatus} receipt={receipt} onRetry={onRetry} onDiscard={onDiscard} />
        )}
      </div>

      {!isDeleted && !isPending && !editing && (
        <div className={styles.msgToolbar} role="toolbar" aria-label="Message actions">
          {QUICK_REACTIONS.slice(0, 4).map((char) => (
            <button
              key={char}
              type="button"
              className={styles.toolbarBtn}
              aria-label={`React with ${emojiInfo(char)?.name || char}`}
              title={emojiInfo(char)?.name}
              onClick={() => react(char)}
            >
              {char}
            </button>
          ))}
          <span className={styles.toolbarDivider} aria-hidden="true" />
          <button type="button" className={`${styles.toolbarBtn} ${picker ? styles.active : ''}`} aria-label="More reactions" title="Add reaction" onClick={openPicker}>
            <SmilePlus size={16} />
          </button>
          {canEdit && (
            <button type="button" className={styles.toolbarBtn} aria-label="Edit message" title="Edit" onClick={startEdit}>
              <Pencil size={15} />
            </button>
          )}
          <button
            type="button"
            className={`${styles.toolbarBtn} ${menu ? styles.active : ''}`}
            aria-label="More actions"
            aria-haspopup="menu"
            aria-expanded={menu}
            onClick={openMenu}
          >
            <MoreHorizontal size={16} />
          </button>
        </div>
      )}

      {picker && (
        <EmojiPicker
          label="Add a reaction"
          onPick={react}
          onClose={() => setPicker(false)}
          style={popStyle}
        />
      )}

      {menu && (
        <div className={styles.dropdown} role="menu" style={{ ...popStyle, minWidth: 230 }}>
          {canEdit && (
            <button type="button" role="menuitem" className={styles.dropdownItem} onClick={startEdit}>
              <Pencil size={15} /><span className={styles.dropdownItemLabel}>Edit message</span>
            </button>
          )}
          {hasText && (
            <button type="button" role="menuitem" className={styles.dropdownItem} onClick={() => copy(message.text, 'Text')}>
              <Copy size={15} /><span className={styles.dropdownItemLabel}>Copy text</span>
            </button>
          )}
          <button type="button" role="menuitem" className={styles.dropdownItem} onClick={() => copy(conversationLink(channel), 'Link')}>
            <Link2 size={15} /><span className={styles.dropdownItemLabel}>Copy link to conversation</span>
          </button>
          {isOwn && links.length > 0 && (
            <button type="button" role="menuitem" className={styles.dropdownItem} onClick={removeLinkPreviews}>
              <Unlink size={15} /><span className={styles.dropdownItemLabel}>Remove link preview</span>
            </button>
          )}
          {isOwn && receipt && (
            <button type="button" role="menuitem" className={styles.dropdownItem} disabled>
              <CheckCheck size={15} />
              <span className={styles.dropdownItemLabel}>
                {receipt.isDM ? (receipt.readers.length ? 'Seen' : 'Not seen yet') : `Seen by ${receipt.readers.length} of ${receipt.othersCount}`}
              </span>
            </button>
          )}
          {isOwn && (
            <>
              <div className={styles.dropdownDivider} />
              <button
                type="button"
                role="menuitem"
                className={`${styles.dropdownItem} ${styles.danger}`}
                onClick={() => { setMenu(false); setConfirmDelete(true); }}
              >
                <Trash2 size={15} /><span className={styles.dropdownItemLabel}>Delete message</span>
              </button>
            </>
          )}
        </div>
      )}

      {confirmDelete && (
        <div className={styles.confirmPop} role="alertdialog" aria-labelledby={`del-${message.id}`} style={popStyle}>
          <span id={`del-${message.id}`} className={styles.confirmTitle}>Delete this message?</span>
          <span className={styles.confirmBody}>
            {isGroup ? `It’s removed for everyone in ${conversationName || 'this conversation'}` : `It’s removed for you and ${conversationName || 'the other person'}`}
            {(images.length + files.length) > 0 ? ', along with its attachments.' : '.'}
          </span>
          <div className={styles.confirmActions}>
            <button type="button" className={`${styles.btnOutline} ${styles.btnSm}`} onClick={() => setConfirmDelete(false)} autoFocus>Cancel</button>
            <button type="button" className={`${styles.btnDanger} ${styles.btnSm}`} onClick={deleteMessage} disabled={busy}>
              {busy ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomMessageItem;
